/**
 * CAR-IA VehicleCard (v3) — the marketplace "cartilla". The single most reused
 * surface (feed, showroom, saved). Data-driven and composable: pass formatted
 * strings + optional trust marks; the card handles layout, photo overlay,
 * favorite toggle, and CTAs. Keep the photo as a real <img> for a11y/SEO.
 */
import React, { useState } from 'react';
import {
  HeartIcon,
  ShareIcon,
  CameraIcon,
  ClockIcon,
  FuelIcon,
  PinIcon,
  MessageIcon,
  PhoneIcon,
} from './icons';
import { PremiumBadge } from './Chips';
import { PriceBlock, SectionHeader, TrustMarkGrid, TrustMark } from './primitives';
import './caria-components.css';

export interface VehicleCardSpec {
  year?: string;
  mileageKm?: string; // formatted, e.g. "76,200"
  fuel?: string;
  location?: string;
  distanceKm?: string; // e.g. "3.2km"
}

export interface VehicleCardProps {
  title: string;
  subtitle?: string;
  priceUsd: string;
  priceSecondary?: string;
  imageUrl?: string;
  photoCount?: number;
  premium?: boolean;
  favorite?: boolean;
  onToggleFavorite?: (next: boolean) => void;
  onShare?: () => void;
  onContact?: () => void;
  onMessage?: () => void;
  onOpen?: () => void;
  /** Chips rendered above the price (TasadorChip, DecisionChip, PriceDropChip…). */
  priceChips?: React.ReactNode;
  spec?: VehicleCardSpec;
  /** When provided, renders the Trust Signals section with the 4 seals. */
  trustMarks?: TrustMark[];
  /**
   * Count of granted trust signals, e.g. "3/4". NOT a percentage (DEF-005) —
   * the 0-100 Trust Score engine (BC-03-E05) is deferred T1.
   */
  trustSignals?: string;
}

export const VehicleCard: React.FC<VehicleCardProps> = ({
  title,
  subtitle,
  priceUsd,
  priceSecondary,
  imageUrl,
  photoCount,
  premium = false,
  favorite = false,
  onToggleFavorite,
  onShare,
  onContact,
  onMessage,
  onOpen,
  priceChips,
  spec,
  trustMarks,
  trustSignals,
}) => {
  const [fav, setFav] = useState(favorite);
  const toggleFav = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !fav;
    setFav(next);
    onToggleFavorite?.(next);
  };

  return (
    <article className={`cia-card${premium ? ' cia-card--premium' : ''}`}>
      <div
        className="cia-photo"
        style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined}
        onClick={onOpen}
        role={onOpen ? 'button' : undefined}
      >
        <div className="cia-photo-top">
          {premium ? <PremiumBadge /> : <span />}
          <div className="cia-photo-actions">
            <button
              className={`cia-photo-action${fav ? ' is-active' : ''}`}
              aria-label="Guardar"
              aria-pressed={fav}
              onClick={toggleFav}
            >
              <HeartIcon />
            </button>
            <button
              className="cia-photo-action"
              aria-label="Compartir"
              onClick={(e) => {
                e.stopPropagation();
                onShare?.();
              }}
            >
              <ShareIcon />
            </button>
          </div>
        </div>
        {photoCount != null && (
          <div className="cia-photo-bot">
            <span className="cia-photo-counter">
              <CameraIcon size={11} />1 / {photoCount}
            </span>
          </div>
        )}
      </div>

      <PriceBlock usd={priceUsd} secondary={priceSecondary} chips={priceChips} />

      <div className="cia-title-block">
        <h2 className="cia-title">{title}</h2>
        {subtitle && <div className="cia-title-sub">{subtitle}</div>}
      </div>

      {spec && (
        <div className="cia-specs">
          {spec.year && (
            <span className="cia-spec">
              <ClockIcon size={12} />
              <span className="cia-spec-num">{spec.year}</span>
            </span>
          )}
          {spec.mileageKm && (
            <>
              <span className="cia-spec-dot" />
              <span className="cia-spec">
                <span className="cia-spec-num">{spec.mileageKm}</span> km
              </span>
            </>
          )}
          {spec.fuel && (
            <>
              <span className="cia-spec-dot" />
              <span className="cia-spec">
                <FuelIcon size={12} /> {spec.fuel}
              </span>
            </>
          )}
          {spec.location && (
            <>
              <span className="cia-spec-dot" />
              <span className="cia-spec">
                <PinIcon size={12} />
                <span className="cia-spec-link">{spec.location}</span>
                {spec.distanceKm && <span className="cia-spec-distance">· {spec.distanceKm}</span>}
              </span>
            </>
          )}
        </div>
      )}

      {trustMarks && (
        <section className="cia-section">
          <SectionHeader
            title="Trust Signals"
            paren="confianza"
            variant="trust"
            trustSignals={trustSignals}
          />
          <TrustMarkGrid marks={trustMarks} />
        </section>
      )}

      {(onContact || onMessage) && (
        <div className="cia-cta-row">
          {onMessage && (
            <button className="cia-cta-ghost" onClick={onMessage}>
              <MessageIcon size={16} />
            </button>
          )}
          {onContact && (
            <button className="cia-cta-primary" onClick={onContact}>
              <PhoneIcon size={15} />
              Contactar
            </button>
          )}
        </div>
      )}
    </article>
  );
};
